import React from 'react';
import { Box, Container, Typography, Card, CardContent, useTheme, useMediaQuery, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const Experience = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const experiences = [
    {
      title: 'Head of Sales & Marketing - African Continent',
      company: 'MIRO Forestry & Products Development Co.',
      period: 'July 2023 - Present',
      icon: <TrendingUpIcon />,
      highlights: [
        'Developing comprehensive sales and marketing strategies across African continent',
        'Leading Key Account Management (KAM) strategy for sustained revenue growth',
        'Overseeing market research and competitive analysis',
        'Managing inventory and production planning integration',
        'Participating in international trade shows and exhibitions',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      title: 'Corporate Business Development Manager',
      company: 'African Group of Companies',
      period: 'January 2015 - June 2023',
      icon: <BusinessIcon />,
      highlights: [
        'Formulated and executed annual, quarterly, and monthly business plans',
        'Conducted comprehensive market analysis for TMT rebar and plywood',
        'Managed end-to-end sales process from inquiry to order negotiation',
        'Coordinated with Product Development team for new product launches',
        'Contributed to product promotion and positioning strategies',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      title: 'Marketing, Dispatch, and Purchase Manager',
      company: 'SAV Industries Ltd.',
      period: 'August 2010 - January 2015',
      icon: <WorkIcon />,
      highlights: [
        'Managed marketing and sales operations nationwide',
        'Oversaw dispatch process ensuring timely product delivery',
        'Managed raw material purchases and vendor negotiations',
        'Led sales operations for two production units',
        'Developed and implemented annual marketing plans and budgets',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      title: 'Assistant Sales Manager - Marketing & Sales',
      company: 'Life Insurance Co. Ltd.',
      period: 'July 2009 - August 2010',
      icon: <WorkIcon />,
      highlights: [
        'Supported sales team in achieving monthly and annual targets',
        'Played active role in advisor recruitment and training',
        'Managed customer relations and lead follow-ups',
        'Coordinated market survey projects',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: 'linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 6 },
            fontWeight: 700,
            background: 'linear-gradient(90deg, #0968E5, #091970)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
          }}
        >
          Professional Experience
        </Typography>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)',
              },
              gap: 4,
            }}
          >
            {experiences.map((exp, index) => (
              <motion.div key={index} variants={cardVariants}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 50px rgba(0,0,0,0.2)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '5px',
                      background: exp.gradient,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        color: '#0968E5',
                      }}
                    >
                      {exp.icon}
                      <Chip
                        label={exp.period}
                        size="small"
                        sx={{
                          ml: 'auto',
                          background: exp.gradient,
                          color: 'white',
                          fontWeight: 400,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                      />
                    </Box>
                    <Typography
                      variant={isMobile ? 'h6' : 'h5'}
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: '#333',
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem', lg: '1.35rem' },
                      }}
                    >
                      {exp.title}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: { xs: 2, md: 3 },
                        color: '#0968E5',
                        fontWeight: 400,
                        fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                      }}
                    >
                      {exp.company}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {exp.highlights.map((highlight, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant={isMobile ? 'body2' : 'body1'}
                          sx={{
                            mb: { xs: 1, md: 1.5 },
                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem', lg: '1rem' },
                            lineHeight: { xs: 1.5, md: 1.6 },
                            color: '#555',
                          }}
                        >
                          {highlight}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Experience;

